'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { Body } from '../typography/Body';
import clsxm from '../utils/style';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from './Chart';
import { FlexContainer } from './FlexContainer';
import { DEFAULT_TIME_SERIES_CHART_HEIGHT } from './timeSeriesLineChart.constants';
import type { ChartConfig } from './Chart';
export interface TimeSeriesLineChartPoint {
  periodStart: string;
  value?: number | null;
  [key: string]: string | number | null | undefined;
}
export interface TimeSeriesLineChartSeries {
  key: string;
  label: string;
  color: string;
}
export interface TimeSeriesLineChartProps {
  data: TimeSeriesLineChartPoint[];
  labelFormatter: (xValue: string) => string;
  valueFormatter: (value: number) => string;
  height?: number;
  series?: TimeSeriesLineChartSeries[];
  yTickFormatter?: (value: number) => string;
  ticks?: string[];
  tickFormatter?: (xValue: string) => string;
}
export interface SeriesTooltipRowProps {
  chartConfig: ChartConfig;
  color: string;
  name: string;
  value: number;
  valueFormatter: (value: number) => string;
}
const singleSeriesChartConfig = {
  value: {
    label: 'Value',
    color: 'var(--color-brand-sky)'
  }
} satisfies ChartConfig;
export function SeriesTooltipRow(t0) {
  const $ = _c(15);
  const {
    chartConfig,
    color,
    name,
    value,
    valueFormatter
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = <div className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{
      backgroundColor: color
    }} />;
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = chartConfig[name]?.label ?? name;
  let t3;
  if ($[2] !== t2) {
    t3 = <Body className="text-content-secondary" size="xs">{t2}</Body>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== value || $[5] !== valueFormatter) {
    t4 = valueFormatter(value);
    $[4] = value;
    $[5] = valueFormatter;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <Body className="font-mono tabular-nums text-content-primary" size="xs" weight="medium">{t4}</Body>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <FlexContainer className="flex-1 gap-4" items="center" justify="between">{t3}{t5}</FlexContainer>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t6) {
    t7 = <FlexContainer className="w-full gap-2" items="center">{t1}{t6}</FlexContainer>;
    $[12] = t1;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export function TimeSeriesLineChart(t0) {
  const $ = _c(54);
  const {
    data,
    labelFormatter,
    valueFormatter,
    height: t1,
    series,
    yTickFormatter,
    ticks: tickValues,
    tickFormatter
  } = t0;
  const height = t1 === undefined ? DEFAULT_TIME_SERIES_CHART_HEIGHT : t1;
  const [isMeasured, setIsMeasured] = useState(false);
  const firstPeriodStart = data[0]?.periodStart;
  const lastPeriodStart = data[data.length - 1]?.periodStart;
  let t2;
  if ($[0] !== firstPeriodStart || $[1] !== lastPeriodStart || $[2] !== tickValues) {
    t2 = tickValues ?? (firstPeriodStart === lastPeriodStart ? firstPeriodStart ? [firstPeriodStart] : [] : [firstPeriodStart, lastPeriodStart].filter(Boolean));
    $[0] = firstPeriodStart;
    $[1] = lastPeriodStart;
    $[2] = tickValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const ticks = t2;
  let t3;
  if ($[4] !== series) {
    t3 = series ? Object.fromEntries(series.map(_temp)) satisfies ChartConfig : singleSeriesChartConfig;
    $[4] = series;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const chartConfig = t3;
  let t4;
  if ($[6] !== data || $[7] !== series) {
    let t5;
    if ($[9] !== data) {
      t5 = t6 => {
        const {
          key: key_0
        } = t6;
        return [key_0, data.reduce((lastIndex, point, index) => typeof point[key_0] === "number" ? index : lastIndex, -1)];
      };
      $[9] = data;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = Object.fromEntries((series ?? []).map(t5));
    $[6] = data;
    $[7] = series;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const lastPointIndexBySeries = t4;
  let t5;
  if ($[11] !== data) {
    t5 = data.reduce(_temp2, -1);
    $[11] = data;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const lastValueIndex = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function handleResize() {
      setIsMeasured(true);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleResize = t6;
  const t7 = isMeasured ? "animate-in fade-in duration-100" : "invisible";
  let t8;
  if ($[14] !== t7) {
    t8 = clsxm("w-full", t7);
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `${height}px`;
  let t10;
  if ($[16] !== t9) {
    t10 = {
      height: t9
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      top: 8,
      right: 0,
      bottom: 0,
      left: 0
    };
    t12 = <CartesianGrid stroke="var(--color-border-secondary)" vertical={false} />;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      fill: "var(--color-content-secondary)",
      fontSize: 11
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== labelFormatter || $[22] !== tickFormatter) {
    t14 = periodStart => (tickFormatter ?? labelFormatter)(String(periodStart));
    $[21] = labelFormatter;
    $[22] = tickFormatter;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== ticks) {
    t15 = <XAxis axisLine={false} dataKey="periodStart" tick={t13} tickFormatter={t14} tickLine={false} ticks={ticks} />;
    $[24] = t14;
    $[25] = ticks;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      fill: "var(--color-content-secondary)",
      fontSize: 11
    };
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== yTickFormatter) {
    t17 = <YAxis axisLine={false} orientation="right" tick={t16} tickFormatter={yTickFormatter} tickLine={false} tickMargin={8} width={48} />;
    $[28] = yTickFormatter;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== chartConfig || $[31] !== series || $[32] !== valueFormatter) {
    t18 = series ? (value, name, item) => <SeriesTooltipRow chartConfig={chartConfig} color={item.color ?? chartConfig[String(name)]?.color ?? "var(--color-content-secondary)"} name={String(name)} value={Number(value)} valueFormatter={valueFormatter} /> : value_0 => valueFormatter(Number(value_0));
    $[30] = chartConfig;
    $[31] = series;
    $[32] = valueFormatter;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== labelFormatter) {
    t19 = periodStart_0 => labelFormatter(String(periodStart_0));
    $[34] = labelFormatter;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = <ChartTooltip content={<ChartTooltipContent formatter={t18} labelFormatter={t19} />} />;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== lastPointIndexBySeries || $[40] !== lastValueIndex || $[41] !== series) {
    t21 = series ? <><ChartLegend content={<ChartLegendContent />} />{series.map(t22 => {
        const {
          key: key_1
        } = t22;
        return <Line connectNulls={true} dataKey={key_1} dot={dotProps => dotProps.index === lastPointIndexBySeries[key_1] ? <circle cx={dotProps.cx} cy={dotProps.cy} fill={`var(--color-${key_1})`} r={4} /> : <g />} isAnimationActive={false} key={key_1} stroke={`var(--color-${key_1})`} strokeWidth={2} type="linear" />;
      })}</> : <Line connectNulls={true} dataKey="value" dot={dotProps_0 => dotProps_0.index === lastValueIndex ? <circle cx={dotProps_0.cx} cy={dotProps_0.cy} fill="var(--color-value)" r={4} /> : <g />} isAnimationActive={false} stroke="var(--color-value)" strokeWidth={2} type="linear" />;
    $[39] = lastPointIndexBySeries;
    $[40] = lastValueIndex;
    $[41] = series;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== data || $[44] !== t15 || $[45] !== t17 || $[46] !== t20 || $[47] !== t21) {
    t22 = <LineChart data={data} margin={t11}>{t12}{t15}{t17}{t20}{t21}</LineChart>;
    $[43] = data;
    $[44] = t15;
    $[45] = t17;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== chartConfig || $[50] !== t10 || $[51] !== t22 || $[52] !== t8) {
    t23 = <ChartContainer className={t8} config={chartConfig} onResize={handleResize} style={t10}>{t22}</ChartContainer>;
    $[49] = chartConfig;
    $[50] = t10;
    $[51] = t22;
    $[52] = t8;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
}
function _temp2(lastIndex_0, point_0, index_0) {
  return typeof point_0.value === "number" ? index_0 : lastIndex_0;
}
function _temp(t0) {
  const {
    key,
    label,
    color
  } = t0;
  return [key, {
    label,
    color
  }];
}
function isChartPoint(point: unknown): boolean {
  return typeof point === 'object' && point !== null && 'periodStart' in point && typeof point.periodStart === 'string' && (!('value' in point) || point.value == null || typeof point.value === 'number') && Object.values(point).every(value => value == null || typeof value === 'string' || typeof value === 'number');
}
function isChartSeries(series: unknown): boolean {
  return typeof series === 'object' && series !== null && 'key' in series && typeof series.key === 'string' && 'label' in series && typeof series.label === 'string' && 'color' in series && typeof series.color === 'string';
}

// LazyLoad exposes unknown props, so validate that boundary before rendering the typed chart.
function isChartProps(props: unknown): props is TimeSeriesLineChartProps {
  return typeof props === 'object' && props !== null && 'data' in props && Array.isArray(props.data) && props.data.every(isChartPoint) && 'labelFormatter' in props && typeof props.labelFormatter === 'function' && 'valueFormatter' in props && typeof props.valueFormatter === 'function' && (!('height' in props) || props.height === undefined || typeof props.height === 'number') && (!('series' in props) || props.series === undefined || Array.isArray(props.series) && props.series.every(isChartSeries)) && (!('yTickFormatter' in props) || props.yTickFormatter === undefined || typeof props.yTickFormatter === 'function') && (!('ticks' in props) || props.ticks === undefined || Array.isArray(props.ticks) && props.ticks.every(tick => typeof tick === 'string')) && (!('tickFormatter' in props) || props.tickFormatter === undefined || typeof props.tickFormatter === 'function');
}
export default function TimeSeriesLineChartBoundary(props) {
  const $ = _c(2);
  if (!isChartProps(props)) {
    throw new Error("Invalid time series line chart props");
  }
  let t0;
  if ($[0] !== props) {
    t0 = <TimeSeriesLineChart {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}